import { resolveActiveAuditOverrides } from './truthAttestations';
/**
 * siteReportAuditor — reusable tier 1/2/3/4 report audit checks.
 * Extracted from scripts/audit/auditSiteReports.ts for client-side R-81.
 */
import type { ReconciliationReport } from '../../types/auditCanonicalFacts';
import { POWER_BUCKET_MIDPOINT_MW } from '../projectIntent';
import { isComparisonBoundPrefix, isNonFacilityCapacityMention, capacityQuantityFieldLabel } from './capacityPolysemy';
import { AREA_MENTION_RE, classifyAcreageMention } from './acreageRoles';
import {
  valuesWithinTolerance,
  type InternalCanonicalKey,
} from './unitNormalize';
import { normalizeStubProse } from '../sectionDepthGate';

export interface ReportDoc {
  id: string;
  reportType: string;
  siteId: string;
  // The fields below are best-effort — actual schema varies per report type.
  executiveSummary?: string | { summary?: string };
  conclusion?: { topStrengths?: string[]; topRisks?: string[]; keyConditions?: string[]; nextActions?: string[]; btmDecisionSummary?: string };
  references?: Array<{ id?: string; field?: string; pageNumber?: number; title?: string }>;
  documentRefs?: Array<{ docId: string; userIncluded?: boolean; tier?: string }>;
  customBriefAnswers?: Array<{ questionId: string; question?: string; questionNumber?: number; findings?: string; evidence?: string; confidence?: string }>;
  customBriefName?: string;
  customBriefQuestions?: Array<{ id: string; text: string }>;
  risks?: Array<{ severity?: string; title?: string; description?: string }>;
  recommendations?: unknown[];
  keyFindings?: Array<{ title?: string; description?: string }>;
  sectionOrder?: string[];
  createdAt?: string | number;
  markdown?: string; // fallback if a `renderedMarkdown` is stored
  [key: string]: unknown;
}

/**
 * S2 — structured value↔source pairing for a Tier-2e internal-number finding.
 * Mirrors `internalNumberReconciler.NumberMention` (subset) so the reconcile UI
 * can show WHERE each number came from. The legacy human-readable `message`
 * string is ALWAYS still emitted alongside this (back-compat for existing
 * tests/UI that read it). `value` is the parsed numeric/string value;
 * `excerpt` is the trimmed source line; `lineNumber` is 1-based into the
 * flattened report text.
 */
export interface Tier2eMention {
  value: string | number;
  sectionId?: string;
  lineNumber?: number;
  excerpt?: string;
}

export interface SiteReportFinding {
  tier: string;
  severity: 'block' | 'warn' | 'info';
  message: string;
  context?: string;
  /** S2 — canonical key for the conflicting fact (Tier-2e / Tier-3). */
  canonicalFactKey?: InternalCanonicalKey;
  /** S2 — structured value↔source pairs backing the finding (Tier-2e / Tier-3). */
  mentions?: Tier2eMention[];
  /**
   * E — structured value-picker candidates for a Tier-3 cross-report
   * divergence, source-of-truth first. Typed data, never parsed back out of
   * the message prose — mirrors `UserAuditFinding.reconcileCandidates`.
   */
  reconcileCandidates?: string[];
  /**
   * Tier-2k only — the normalized prose the finding observed repeated.
   *
   * This is the finding's identity for the overlap test in `runUserAudit`: the
   * R-73 depth gate detects the same repetition in a section-id namespace that
   * cannot be mapped onto this one, so the two are compared by CONTENT. Set
   * through `normalizeStubProse` so both sides are byte-comparable.
   */
  stubProse?: string;
  /**
   * Tier-2k only — the sections this finding observed carrying `stubProse`,
   * in the auditor's raw report-FIELD namespace.
   *
   * Structured because `runUserAudit` folds these names into the surviving
   * R-73 row when the overlap skip drops this finding; parsing them back out
   * of `message` would promote the prose to a data contract. Like
   * `stubProse`, in-memory only — neither field reaches `UserAuditFinding`.
   */
  stubSectionIds?: string[];
}

/**
 * S1 — phase-aware capacity source-of-truth, derived from the project intent's
 * effective-phases view. Threaded into `checkTier2eInternalNumbers` so the
 * detector can corroborate co-existing phase-ramp MW values against the SSOT
 * instead of flagging them as a conflict.
 *
 *  - `ultimateMW`  — the project ULTIMATE total (`effectivePhases().totalCapacityMW`,
 *                    which is `Math.max` over finite cumulative phase values —
 *                    NEVER the sum). Undefined when no phase carries a capacity.
 *  - `phaseRampMW` — the per-phase CUMULATIVE capacities (each phase's total site
 *                    capacity AT its completion, the codebase's convention). A
 *                    report citing any value on this ramp is consistent, not a
 *                    conflict.
 */
export interface CapacitySsot {
  ultimateMW?: number;
  phaseRampMW: number[];
  /**
   * RCA 2026-08-13 (Redhead) — the REST of the phase model. Reports narrate
   * more phase-plan quantities than the cumulative milestones: Phase 1 was
   * bucket-mode (`capacityBucket: '10-50mw'`, `capacityMW: null`) with its
   * grid component sized at 50 MW, Phase 3's grid allocation was 300 MW,
   * and the intent-level requirement bucket topped at 500 — so reports
   * citing 50/300/500 were flagged against a ramp that only knew [350, 550].
   * Every value here is still strictly INTENT-DERIVED (bucket endpoints,
   * per-phase power-mix component sizes, consecutive-milestone deltas);
   * arbitrary numbers still fail corroboration.
   */
  auxiliaryMW?: number[];
}

/** Endpoints of a capacity-bucket id ("10-50mw" → [10, 50]; "under-10mw" →
 *  [10]; "over-1500mw" → [1500]) PLUS the closed-bucket MIDPOINT — the
 *  platform's sizing basis (POWER_BUCKET_MIDPOINT_MW): financial/BoM/phase-
 *  feasibility surfaces print it as "FEASIBLE (at 30 MW sizing basis)", so a
 *  report legitimately cites it (live 2026-08-13: Redhead P1 bucket 10-50 →
 *  "30" flagged as a foreign total-capacity value). Intent-derived, so it
 *  belongs in the anchor set; arbitrary numbers still fail corroboration.
 *  Parsed generically so future buckets work. */
function bucketEndpointsMW(bucket: string | null | undefined): number[] {
  if (!bucket) return [];
  // Authoritative sizing basis first (covers non-midpoint rules like legacy
  // over-200mw → 300); generic endpoint parse keeps future buckets working.
  const basis = (POWER_BUCKET_MIDPOINT_MW as Record<string, number>)[bucket];
  const out: number[] = basis !== undefined ? [basis] : [];
  const closed = bucket.match(/^(\d+)-(\d+)mw$/i);
  if (closed) {
    const lo = Number(closed[1]);
    const hi = Number(closed[2]);
    out.push(lo, hi);
    if (basis === undefined) out.push((lo + hi) / 2);
    return out;
  }
  const open = bucket.match(/^(?:under|over)-(\d+)mw$/i);
  if (open) out.push(Number(open[1]));
  return out;
}

/**
 * Recorded reconcile decisions keyed under BOTH the public and internal
 * canonical-key spellings (Tier-2e reads the internal key). Shared by
 * runUserAudit and runFullAudit (2026-08-14 parity fix) so the publish gate
 * cannot re-block a fact the user already settled. Pure.
 */
export function reconcileOverridesFrom(
  auditOverrides: readonly unknown[] | undefined,
  toInternal: (key: string) => string | undefined,
): Map<string, string | number> {
  const out = new Map<string, string | number>();
  const active = resolveActiveAuditOverrides(auditOverrides as readonly import('../../types/site').SiteAuditOverride[] | undefined);
  for (const o of active) {
    const ov = o as { type?: string; canonicalKey?: string; canonicalValue?: string | number };
    if (ov?.type !== 'reconcile' || !ov.canonicalKey || ov.canonicalValue === undefined) continue;
    out.set(ov.canonicalKey, ov.canonicalValue);
    const internal = toInternal(ov.canonicalKey);
    if (internal) out.set(internal, ov.canonicalValue);
  }
  return out;
}

/** Optional per-report audit context (S1). */
export interface ReportAuditContext {
  capacitySsot?: CapacitySsot;
  /**
   * User-recorded reconcile decisions keyed by INTERNAL canonical key
   * (`capacity_mw`), from `site.auditOverrides`. A fact with a recorded
   * canonical is SETTLED: the Tier-2e conflict demotes to info (never
   * silent — the note says the canonical applies on next regeneration).
   * Live 2026-08-11 (Redhead): this detector ran override-blind in parallel
   * with the R-67 gate, so a recorded canonical cleared the cross-report
   * R-79 finding while the per-report Tier-2e conflict kept blocking.
   */
  reconcileOverrides?: ReadonlyMap<string, string | number>;
}

export interface ReportAudit {
  reportId: string;
  reportType: string;
  findings: SiteReportFinding[];
  precedenceViolations?: Array<{
    fact: string;
    reportedValue: string | number;
    tier1Value: string | number;
    tier1Source: string;
    severity: string;
    suggestedFix: string;
  }>;
  internalNumberFindings?: Array<{
    canonicalKey: string;
    label: string;
    severity: 'block' | 'warn';
    mentions: Array<{
      value: string | number;
      normalizedValue: string | number;
      sectionId?: string;
      lineNumber?: number;
      excerpt?: string;
      tier1Cited?: boolean;
    }>;
    conflictingValues: Array<string | number>;
    resolution?: {
      canonicalValue: string | number;
      source: string;
      tier1Source?: string;
    };
    autocorrectBlocked?: boolean;
  }>;
  stats: {
    bodyChars: number;
    sections: number;
    refNCount: number;
    perFilenameCount: number;
    placeholders: number;
  };
}

export interface SiteReportAuditResult {
  siteId: string;
  siteName?: string;
  intent?: {
    powerRequirementMW?: number;
    /**
     * Ultimate campus capacity (MW) for PHASED sites — the final-phase
     * cumulative value from `effectivePhases().totalCapacityMW`. Preferred
     * over `powerRequirementMW` as the canonical-fact truth when present,
     * because phased intents often leave `powerRequirementMW` null.
     */
    totalCapacityMW?: number;
    coolingStrategy?: string;
    workloadProfile?: string;
  };
  sed?: { sedStatus?: string; metadata?: { lastBootstrapAt?: number } };
  reports: ReportAudit[];
  crossReportFindings: SiteReportFinding[];
  /** R-79 — canonical fact reconciler output (post Tier 4). */
  canonicalReconciliation?: ReconciliationReport;
  /** R-65 — intent vs SED narrative reconciliation at audit time. */
  intentSedReconciliation?: {
    aligned: boolean;
    divergences: Array<{
      field: string;
      intentValue: string;
      sedValue: string;
      severity: string;
    }>;
  };
}

/* ──────────────────────────────────────────────────────────────────────
 * Tier 2l — Section depth thresholds (per-report-type)
 * ────────────────────────────────────────────────────────────────────── */

const SECTION_DEPTH: Record<string, Record<string, number>> = {
  feasibility: { executiveSummary: 600, conclusion: 400 },
  environmental: { executiveSummary: 600, conclusion: 400 },
  'power-availability': { executiveSummary: 600, conclusion: 400 },
  'test-fit': { executiveSummary: 600 },
  'supply-chain-risk': { executiveSummary: 600 },
  'custom-brief': { executiveSummary: 600, conclusion: 400 },
  'public-sentiment': {
    executive: 600, stakeholderMap: 200, perBtmSentiment: 200, themeDeepDives: 200,
    trendAnalysis: 200, comparableSiteBenchmark: 200, mitigationPlaybook: 200, appendix: 100,
  },
  'executive-summary': { executiveSummary: 600, conclusion: 400 },
  'water-resource': { executiveSummary: 600 },
  interconnection: { executiveSummary: 600 },
};

/* ──────────────────────────────────────────────────────────────────────
 * Tier 2d — Per-report-type expected vocabulary (cheapest topical-drift check)
 * ────────────────────────────────────────────────────────────────────── */

const EXPECTED_VOCAB: Record<string, RegExp[]> = {
  feasibility: [/\b(viability|feasibility|executability|verdict|critical[\s-]?path|buildable)\b/i],
  'test-fit': [/\b(footprint|setback|parking|stormwater|phasing|buildable)\b/i],
  'supply-chain-risk': [/\b(BoM|bill of materials|lead[\s-]?time|OEM|backlog|SRA|gas turbine|recip|battery|BESS|transformer)\b/i],
  environmental: [/\b(wetland|NEPA|flood|FEMA|USFWS|USACE|critical habitat|air permit|tribal)\b/i],
  'power-availability': [/\b(interconnection|IX|substation|transmission|MISO|PJM|ERCOT|FERC|BTM)\b/i],
  'water-resource': [/\b(water (rights|withdrawal|supply)|aquifer|cooling water|WRI|Aqueduct|LPDES|NPDES)\b/i],
  // N (2026-08-12) — this report type renders as the CONNECTIVITY Feasibility
  // Study (fiber routes, IX distance, latency), but the vocab expected grid-
  // interconnection terms (IA, capacity letter, system impact study), so a
  // well-formed connectivity report scored 1 hit and warned as "topical
  // drift". Expect the vocabulary the reports actually use.
  interconnection: [/\b(interconnection|IA\b|capacity letter|system impact study|transmission upgrade|queue|fiber|fibre|latency|IX\b|peering|carrier|long[\s-]?haul|dark fiber|backbone|lateral)\b/i],
  'public-sentiment': [/\b(community|stakeholder|opposition|public hearing|regulatory docket|NGO|tribal)\b/i],
  'custom-brief': [/\b(question|diligence|red flag|findings?|evidence|confidence)\b/i],
  'executive-summary': [/\b(synthesis|cross[\s-]?report|verdict|critical risks?|next steps?|recommendation)\b/i],
};

const FORBIDDEN_VOCAB: Record<string, RegExp[]> = {
  'test-fit': [/\breviewer (feedback|comments)\b/i, /\bplease (revise|update)\b/i, /\bgas turbine (supply|lead[\s-]?time)\b/i],
  'supply-chain-risk': [/\bUSACE Section 404\b/i, /\bFEMA flood zone\b/i],
  feasibility: [/\breviewer feedback\b/i, /\bplease revise\b/i],
  'executive-summary': [/\breviewer (feedback|comments)\b/i, /\bplease (revise|update)\b/i],
};

/* ──────────────────────────────────────────────────────────────────────
 * Canonical-fact extractors (Tier 3 cross-report)
 * ────────────────────────────────────────────────────────────────────── */

interface CanonicalFact {
  id: 'totalCapacityMW' | 'siteAcreage' | 'wetlandAcreage' | 'nearestIxKm' | 'fema100yrFloodZone';
  label: string;
  contextRe: RegExp;
  valueRe: RegExp;
  toNumber?: (s: string) => number;
  tolerancePct: number;
  /**
   * S1/S3 — the internal canonical key for `valuesWithinTolerance` /
   * `normalizeToCanonical`. When set, Tier-2e routes numeric comparisons
   * through `unitNormalize` (unit-aware tolerance) instead of the local flat
   * `tolerancePct`. Distance facts additionally use this to convert mi→km, etc.
   */
  internalKey?: InternalCanonicalKey;
  /**
   * S1/S3 — multi-valued facts legitimately carry several co-existing real
   * values in one report (phase capacity alongside the ultimate build-out, a
   * buildable footprint alongside the total parcel, a sub→site distance
   * alongside a sub→IX distance). For these we use R-78-style BEST-MATCH
   * corroboration + a monotonic-ramp fallback, NOT a flat distinct-set. A
   * single-valued fact (FEMA flood zone) cited two ways is still a hard
   * conflict and keeps the STRICT distinct-set check.
   */
  multiValue?: boolean;
  /**
   * S3 — mirror R-78 WARN_ONLY: an unreconciled multi-value drift here is a
   * WARNING, not a hard block (geometry estimates routinely differ from the
   * cited buildable footprint).
   */
  warnOnly?: boolean;
  /**
   * K — MW-quantity polysemy guard. A line whose context matches this pattern
   * is talking about a DIFFERENT quantity that happens to share the fact's
   * unit (grid headroom in MW, a fiber-lateral distance in km) and must not
   * enter the fact's value set — as a claim OR as corroboration. Redhead
   * live case: "~50 MW of indicated available grid capacity" was extracted
   * as a facility-capacity claim and blocked six reports.
   */
  negativeContextRe?: RegExp;
}

/**
 * K — a numeric range ("200–500 MW", "4-7 million") is a BAND, not a value
 * claim; extracting its endpoints manufactures conflicts against the resolved
 * capacity. Matched against the text immediately before the captured value.
 */
const RANGE_VALUE_RE = /[\d,]+(?:\.\d+)?\s*[-–—]\s*$/;

/** True when the captured match is the upper end of an explicit range. */
function isRangeEndpoint(line: string, matchIndex: number): boolean {
  return RANGE_VALUE_RE.test(line.slice(0, matchIndex));
}

/**
 * Adversarial review 2026-08-13 — the polysemy guard vetoes a WINDOW around
 * the matched value, not the whole line: "The 1,170-acre site includes 65.7
 * acres of wetlands" must veto only the wetland sub-value, not every number
 * that happens to share the line with a guard word. 80 chars behind covers
 * the live guard clauses ("~50,000 Dth/day … which would support 300 MW");
 * 40 ahead covers trailing qualifiers ("(range bucket)", "of available grid
 * capacity").
 */
function negativeContextNearMatch(
  negativeContextRe: RegExp | undefined,
  line: string,
  matchIndex: number | undefined,
  matchLength: number,
): boolean {
  if (!negativeContextRe) return false;
  if (typeof matchIndex !== 'number') return negativeContextRe.test(line);
  const window = line.slice(Math.max(0, matchIndex - 80), matchIndex + matchLength + 40);
  return negativeContextRe.test(window);
}

/** Keep the legacy fact matching for other quantities. Acres must also
 * identify gross site land: regional wetlands, net land and study footprints
 * share its unit but cannot corroborate or contradict that denominator.
 */
function canonicalFactMatches(fact: CanonicalFact, line: string): RegExpMatchArray[] {
  if (fact.id === 'totalCapacityMW') {
    return [...line.matchAll(new RegExp(fact.valueRe.source, 'gi'))]
      .filter((match) => !isNonFacilityCapacityMention(line, match.index, match[0].length));
  }
  if (fact.id !== 'siteAcreage') {
    const match = line.match(fact.valueRe);
    return match ? [match] : [];
  }
  // Classification alone ignores Markdown emphasis/code delimiters. Replace
  // them with spaces of the same length so raw match offsets and quotes stay
  // intact; intraword punctuation is not assumed to be a formatting delimiter.
  const classificationLine = line.replace(/[*_`]+/g, (marks: string, offset: number) =>
    /\w/.test(line[offset - 1] ?? '') && /\w/.test(line[offset + marks.length] ?? '')
      ? marks : ' '.repeat(marks.length));
  return [...line.matchAll(new RegExp(AREA_MENTION_RE.source, AREA_MENTION_RE.flags))]
    .filter((match) => {
      // Preserve this detector's acre-only units; no implicit hectare conversion.
      if (!/^(?:acres?|ac\.?)$/i.test(match[2] ?? '')) return false;
      const role = classifyAcreageMention(classificationLine, match.index!, match[0].length);
      return role?.key === 'acreage' && role.scope === 'site-gross';
    });
}

function canonicalFactExcerpt(fact: CanonicalFact, line: string, match: RegExpMatchArray): string {
  if (fact.id !== 'siteAcreage') return line.trim().slice(0, 600);
  const index = match.index ?? 0;
  // A long line can contain several distinct areas. Quote this matched number,
  // rather than the unrelated number at the beginning of that line.
  return line.slice(Math.max(0, index - 180), index + match[0].length + 180).trim();
}

const CANONICAL_FACTS: CanonicalFact[] = [
  {
    id: 'totalCapacityMW', label: 'Total capacity (MW)',
    contextRe: /(\bMW\b|capacity|power requirement|total power)/i,
    valueRe: /\b([\d,]+(?:\.\d+)?)\s*MW\b/i,
    toNumber: (s) => parseFloat(s.replace(/,/g, '')),
    tolerancePct: 1,
    internalKey: 'capacity_mw',
    multiValue: true,
    // K — FACILITY capacity only. Grid headroom/import, BTM/on-site
    // generation, substation/interconnection allocations, intent range
    // buckets AND fuel-supply potential ("~50,000 Dth/day … would support
    // 300 MW") are different MW quantities: they may never conflict with
    // (or corroborate) the facility capacity.
    negativeContextRe: /(grid (capacity|headroom|import)|available (grid )?(capacity|power)|power indication|spare (capacity|headroom)|headroom|import (capacity|allocation)|interconnection (capacity|allocation|service)|substation (capacity|rating)|BTM (generation|capacity|output)|on-?site generation|backup (power|generation|capacity)|generat(?:or|ion) (capacity|output)|range bucket|capacity range|Dth|MMBtu|MMcf|gas (supply|transport|availability)|fuel supply|transport contract|enough (gas|fuel))/i,
  },
  {
    id: 'siteAcreage', label: 'Site acreage',
    contextRe: /(site (area|acreage)|parcel|footprint)/i,
    valueRe: /\b([\d,]+(?:\.\d+)?)\s*(?:ac|acres?)\b/i,
    toNumber: (s) => parseFloat(s.replace(/,/g, '')),
    tolerancePct: 5,
    internalKey: 'acreage',
    multiValue: true,
    warnOnly: true,
  },
  {
    id: 'wetlandAcreage', label: 'Wetland acreage',
    contextRe: /wetland/i,
    // Proximity-anchored (2026-08-12): the acres value must FOLLOW the word
    // "wetland" within the clause AND not belong to a floodplain clause. The
    // old any-acres-on-a-wetland-line match captured the FLOODPLAIN acreage
    // from "65.7 acres of wetlands and 325.51 acres in the 100-year
    // floodplain" and manufactured a 65.7-vs-325.51 cross-report conflict
    // (live Redhead). A line whose only candidate is floodplain-qualified
    // contributes nothing — conservative beats wrong.
    valueRe: /\bwetlands?\b[^.\n]{0,60}?\b([\d,]+(?:\.\d+)?)\s*(?:ac|acres?)\b(?![^.\n]{0,40}?(?:floodplain|flood\s*zone|100-year))/i,
    toNumber: (s) => parseFloat(s.replace(/,/g, '')),
    tolerancePct: 10,
  },
  {
    id: 'nearestIxKm', label: 'Nearest IX/peering distance (km)',
    // L — IX/peering ONLY. The old `interconnect` context swept GRID
    // interconnection prose into this fact, so a substation distance (2.9 km),
    // a fiber-lateral length (5 mi) and the true IX distance (204.9 km) all
    // landed in one bucket and "disagreed". Substation distance has its own
    // tier-1-backed fact in the R-78/R-79 layer; fiber laterals are not a
    // canonical fact at all.
    contextRe: /(peering|\bIX\b|internet exchange|peer(?:ing)?\s+exchange)/i,
    valueRe: /\b(\d+(?:\.\d+)?)\s*(km|mi|miles?)\b/i,
    toNumber: (s) => parseFloat(s),
    tolerancePct: 5,
    internalKey: 'interconnection_distance_km',
    // L — belt-and-braces: even on an IX/peering line, a substation or
    // fiber-lateral clause is a different distance — and a SEARCH-RADIUS
    // phrase ("no facilities within a 50 km radius, per PeeringDB") is a
    // survey bound, not a distance claim (live Redhead: the 50 kept a
    // within-report IX "inconsistency" alive against the true 204.9).
    // Baton Rouge (2026-08-18): "0 DC facilities within 50 km; closest IX
    // DartPoints…" — no article, no "radius" — slipped past `within\s+an?\s`
    // and surfaced a cross-report "204.9 vs 50" disagreement. "within N km"
    // is a BOUND (≤N) whether or not an article precedes the number, so the
    // guard now accepts an optional article before the digit.
    negativeContextRe: /(substation|fiber|fibre|lateral|gas|pipeline|within\s+(?:an?\s+)?\d|radius)/i,
    // S3 — multi-value: a substation→site distance and a substation→IX/peering
    // distance legitimately co-exist in one report. WARN-only (like acreage):
    // an unreconciled pair is a review signal, not a hard publish block —
    // distance has no within-report phase SSOT to corroborate the closer value.
    multiValue: true,
    warnOnly: true,
  },
  {
    id: 'fema100yrFloodZone', label: 'FEMA 100-yr flood zone',
    contextRe: /(flood|FEMA|SFHA)/i,
    valueRe: /\bzone\s+(A(?:E|H|O)?|AE|AO|AH|VE|V|X|D|B|C)\b/i,
    tolerancePct: 0,
    internalKey: 'flood_zone',
    // Single-valued by design: one site has one flood zone. NOT multiValue — a
    // second distinct zone in the same report is a genuine conflict (S3).
  },
];

/* ──────────────────────────────────────────────────────────────────────
 * S1 — capacity SSOT helper (shared by both audit entry points)
 * ────────────────────────────────────────────────────────────────────── */

/**
 * Derive the phase-aware capacity source-of-truth from a project intent's
 * effective-phases view. Returns the ultimate (Math.max cumulative) total plus
 * the per-phase cumulative ramp values (finite, >0). Used to corroborate
 * co-existing MW mentions in Tier-2e instead of flagging them as a conflict.
 *
 * NOTE: kept signature-loose (`{ totalCapacityMW?, phases? }`) so callers can
 * pass `effectivePhases(intent)` directly without importing the audit module's
 * types. Returns `{ phaseRampMW: [] }` when there is no usable capacity.
 */
export function capacitySsotFromPhases(
  view: {
    totalCapacityMW?: number | null;
    phases?: Array<{
      capacityMW?: number | null;
      capacityBucket?: string | null;
      powerSourceConfig?: {
        componentParams?: Partial<Record<string, { capacityMW?: number } | undefined>>;
      } | null;
    }>;
  } | null | undefined,
  opts?: {
    /** Intent-level requirement bucket ("200-500mw") — its endpoints are
     *  legitimate narrated quantities ("the 200–500 MW program"). */
    requirementBucket?: string | null;
  },
): CapacitySsot {
  if (!view) return { phaseRampMW: [], auxiliaryMW: [] };
  const phases = view.phases ?? [];
  const phaseRampMW = phases
    .map((p) => p.capacityMW)
    .filter((mw): mw is number => typeof mw === 'number' && Number.isFinite(mw) && mw > 0);
  const ultimateMW = typeof view.totalCapacityMW === 'number'
    && Number.isFinite(view.totalCapacityMW)
    && view.totalCapacityMW > 0
    ? view.totalCapacityMW
    : undefined;

  // RCA 2026-08-13 — the rest of the phase model, all intent-derived:
  const auxiliaryMW: number[] = [];
  for (const p of phases) {
    // Bucket-mode phases (capacityMW null, capacityBucket set) — endpoints.
    auxiliaryMW.push(...bucketEndpointsMW(p.capacityBucket));
    // Per-phase power-mix component sizes (grid 50/300, gas 400, recip 100)
    // — the quantities the power-availability narrative is BUILT from.
    for (const comp of Object.values(p.powerSourceConfig?.componentParams ?? {})) {
      const mw = comp?.capacityMW;
      if (typeof mw === 'number' && Number.isFinite(mw) && mw > 0) auxiliaryMW.push(mw);
    }
  }
  // Consecutive-milestone deltas ("Phase 3 adds 200 MW").
  for (let i = 1; i < phaseRampMW.length; i++) {
    const delta = phaseRampMW[i]! - phaseRampMW[i - 1]!;
    if (delta > 0) auxiliaryMW.push(delta);
  }
  auxiliaryMW.push(...bucketEndpointsMW(opts?.requirementBucket));

  return { ultimateMW, phaseRampMW, auxiliaryMW };
}

/**
 * A — the ONE anchor set both capacity detectors corroborate against:
 * ultimate + every per-phase cumulative ramp value + (for ≥2 phases) the
 * ramp sum, the documented Holly-Ridge/Redhead double-count echo. Shared by
 * Tier-2e (within-report) and Tier-3 (cross-report) so the two layers can
 * never disagree about what counts as phase-consistent again.
 */
export function capacityAnchors(ssot: CapacitySsot | undefined): number[] {
  if (!ssot) return [];
  const anchors: number[] = [];
  if (ssot.ultimateMW != null) anchors.push(ssot.ultimateMW);
  anchors.push(...ssot.phaseRampMW);
  // The ramp-sum echo covers ONLY cumulative milestones (the documented
  // double-count artifact) — auxiliary quantities never sum.
  if (ssot.phaseRampMW.length >= 2) {
    anchors.push(ssot.phaseRampMW.reduce((s, v) => s + v, 0));
  }
  anchors.push(...(ssot.auxiliaryMW ?? []));
  return anchors;
}

/* ──────────────────────────────────────────────────────────────────────
 * Helpers
 * ────────────────────────────────────────────────────────────────────── */

function flattenToText(v: unknown): string {
  if (v == null) return '';
  if (typeof v === 'string') return v;
  if (Array.isArray(v)) return v.map(flattenToText).join('\n');
  if (typeof v === 'object') return Object.entries(v as Record<string, unknown>).map(([key, value]) => {
    const label = capacityQuantityFieldLabel(key);
    const text = flattenToText(value);
    return label && (typeof value === 'string' || typeof value === 'number')
      ? `${label}: ${text}` : text;
  }).join('\n');
  return String(v);
}

function extractSections(report: ReportDoc): Array<{ id: string; content: string }> {
  const out: Array<{ id: string; content: string }> = [];
  const r = report as unknown as Record<string, unknown>;
  const knownStringSections = [
    'executiveSummary', 'summary', 'sitingNotes', 'developmentPathway', 'projectIntentOverview',
    'executive', 'stakeholderMap', 'perBtmSentiment', 'themeDeepDives', 'trendAnalysis',
    // J — the PSR persists its appendix as `sentimentAppendix` (the alias
    // table in sectionDepthGate maps appendix → [sentimentAppendix,
    // appendix]); reading only `appendix` here made the Tier-2l appendix
    // check structurally unpassable for every populated PSR.
    'comparableSiteBenchmark', 'mitigationPlaybook', 'appendix', 'sentimentAppendix',
    // Adversarial review 2026-08-13: this list had drifted behind
    // canonicalFactReconciler's — test-fit's constructability/cost/capacity
    // sections were invisible to every text-based tier check here, and a
    // report whose ONLY populated content was one of these fields would
    // wrongly classify as a body-less shell (isReportBodyEmpty on an empty
    // extraction is vacuously true). Keep the two lists in lockstep.
    'constructabilityAssessment', 'costRangeEstimate', 'maxCampusCapacity',
  ];
  const knownObjectSections = [
    'conclusion', 'gridStudy', 'transportStudy', 'connectivityStudy', 'climateSevereWeather',
    'extendedData', 'btmOverview', 'nepaScreening', 'infrastructureInterconnection', 'insights',
    'projectContext', 'powerAnalysis', 'interconnectionAnalysis',
    // 2026-08-13 (Redhead water RCA): the water report's REAL narrative lives
    // in `waterAnalysis` (structured, refIds-cited) and `editorialNarrative`
    // (13 [Ref N] cites) — both invisible here, so Tier-2b divided ZERO
    // visible cites by the generic fields' paragraphs and warned on a richly
    // cited report. Keep in lockstep with canonicalFactReconciler.
    'waterAnalysis', 'editorialNarrative',
  ];
  const knownArraySections = ['keyFindings', 'risks', 'recommendations', 'criticalRisks', 'recommendedNextSteps'];

  for (const k of knownStringSections) {
    // Compatibility aliases of the same Public Sentiment chapter are not
    // additional displayed paragraphs. Different alias text stays audited.
    if (report.reportType === 'public-sentiment' && typeof r[k] === 'string'
      && ((['summary', 'executiveSummary'].includes(k) && r[k] === r.executive)
        || (k === 'appendix' && r[k] === r.sentimentAppendix))) continue;
    if (typeof r[k] === 'string') out.push({ id: k, content: r[k] as string });
  }
  for (const k of knownObjectSections) {
    if (r[k] && typeof r[k] === 'object') out.push({ id: k, content: flattenToText(r[k]) });
  }
  for (const k of knownArraySections) {
    if (Array.isArray(r[k])) out.push({ id: k, content: flattenToText(r[k]) });
  }
  for (const ans of report.customBriefAnswers ?? []) {
    out.push({
      id: `customBriefQ_${ans.questionId}`,
      content: [ans.question, ans.findings, ans.evidence].filter(Boolean).join('\n'),
    });
  }
  return out;
}

function reportFullText(report: ReportDoc): string {
  if (typeof report.markdown === 'string' && report.markdown.length > 100) return report.markdown;
  return extractSections(report).map((s) => `# ${s.id}\n${s.content}`).join('\n\n');
}

/* ──────────────────────────────────────────────────────────────────────
 * Tier checks
 * ────────────────────────────────────────────────────────────────────── */

function checkTier1HardFails(report: ReportDoc, findings: SiteReportFinding[]): void {
  const text = reportFullText(report);

  // [unavailable] reference markers
  const unavailableMatches = text.match(/\[unavailable\]/gi) ?? [];
  if (unavailableMatches.length > 0) {
    findings.push({
      tier: 'Tier 1 — R-2', severity: 'block',
      message: `${unavailableMatches.length} \`[unavailable]\` reference marker(s) in body — known R-2 gap.`,
    });
  }

  // R-29: NRC nuclear fields (only relevant if report mentions reactors)
  if (/nuclear|reactor|NRC/i.test(text)) {
    const hasNameplate = /\bnameplate\b|\b\d{2,5}\s*MW\b/i.test(text);
    const hasCapacityFactor = /capacity factor|CF\b/i.test(text);
    if (hasCapacityFactor && !hasNameplate) {
      findings.push({
        tier: 'Tier 1 — R-29', severity: 'warn',
        message: 'Mentions capacity factor without nameplate MW — R-29 risk (nuclear plants must surface nameplate MW alongside CF%).',
      });
    }
  }
}

/**
 * Structured citations (2026-08-13, Redhead water RCA) — modern report
 * sections attach sources as `refIds: [n, …]` arrays on content blocks
 * instead of inline `[Ref N]` prose markers. Walk the audited sections and
 * count every finite entry so a structurally-cited report is not measured
 * as uncited. Bounded depth guards against pathological nesting.
 */
function countStructuredRefIds(value: unknown, maxRef: number | null, depth = 0): number {
  if (depth > 8 || value == null || typeof value !== 'object') return 0;
  let n = 0;
  if (Array.isArray(value)) {
    for (const v of value) n += countStructuredRefIds(v, maxRef, depth + 1);
    return n;
  }
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    if (k === 'refIds' && Array.isArray(v)) {
      // A refId is a 1-based index into report.references — only entries
      // that RESOLVE count as citations (Codex review on #135: refIds
      // [0, 999] must not launder an uncited report past the density
      // floor). Without a references array, positive integers stand.
      n += v.filter((x) => {
        const num = Number(x);
        if (!Number.isInteger(num) || num < 1) return false;
        return maxRef == null || num <= maxRef;
      }).length;
    } else {
      n += countStructuredRefIds(v, maxRef, depth + 1);
    }
  }
  return n;
}

/** 1-based upper bound of resolvable refIds — `report.references` length
 *  when present, null (positive-int check only) otherwise. */
function maxResolvableRefId(report: ReportDoc): number | null {
  const refs = (report as unknown as { references?: unknown }).references;
  return Array.isArray(refs) && refs.length > 0 ? refs.length : null;
}

function checkTier2bCitationDensity(report: ReportDoc, findings: SiteReportFinding[]): void {
  const text = reportFullText(report);
  const refCount = (text.match(/\[Ref\s+[^\]]+\]/gi) ?? []).length;
  const perCount = (text.match(/\(per\s+"[^"]+"\)/gi) ?? []).length;
  // Modern numeric markers — "[5]" / "[5, 6]" — used by the report reader's
  // superscript citations. Legacy [Ref N] and (per "…") counted above.
  const numericCount = (text.match(/\[\d{1,3}(?:,\s*\d{1,3})*\]/g) ?? []).length;
  // Structured refIds attached to section content blocks (water RCA: the
  // live report carried refIds-cited waterAnalysis + a 13-cite
  // editorialNarrative and still measured 0.00).
  const structuredCount = countStructuredRefIds(
    Object.fromEntries(extractSections(report)
      .map((s) => [s.id, (report as unknown as Record<string, unknown>)[s.id]])),
    maxResolvableRefId(report),
  );
  const paragraphs = text.split(/\n\s*\n/).filter((p) => p.trim().length > 80).length;

  if (paragraphs > 0) {
    const totalCites = refCount + perCount + numericCount + structuredCount;
    const density = totalCites / paragraphs;
    if (density < 0.5) {
      findings.push({
        tier: 'Tier 2b — Citation density', severity: 'warn',
        message: `Citation density ${density.toFixed(2)} (${totalCites} cites / ${paragraphs} substantive paragraphs). Threshold: ≥ 0.5.`,
      });
    }
  }
}

function checkTier2dTopicalDrift(report: ReportDoc, findings: SiteReportFinding[]): void {
  const text = reportFullText(report);
  const expected = EXPECTED_VOCAB[report.reportType] ?? [];
  const forbidden = FORBIDDEN_VOCAB[report.reportType] ?? [];

  let hits = 0;
  for (const re of expected) {
    const matches = text.match(new RegExp(re.source, 'gi'));
    if (matches) hits += matches.length;
  }
  if (expected.length > 0 && hits < 4) {
    findings.push({
      tier: 'Tier 2d — Topical drift (keywords)', severity: 'warn',
      message: `Only ${hits} expected-vocab hits for ${report.reportType}. Threshold: ≥ 4. Indicates possible topical drift.`,
    });
  }

  for (const re of forbidden) {
    const match = text.match(re);
    if (match) {
      findings.push({
        tier: 'Tier 2d — Cross-type bleed', severity: 'block',
        message: `Forbidden vocab "${match[0]}" detected — indicates cross-report-type content leak.`,
        context: text.slice(Math.max(0, (match.index ?? 0) - 60), (match.index ?? 0) + 120),
      });
    }
  }
}

interface Tier2eValue {
  value: string | number;
  line: string;
  lineNumber: number;
}

/** Local distinct-bucket using the fact's tolerance / canonical normalization. */
function valuesEquivalent(fact: CanonicalFact, a: string | number, b: string | number): boolean {
  if (typeof a === 'number' && typeof b === 'number') {
    if (fact.internalKey) return valuesWithinTolerance(fact.internalKey, a, b);
    if (a === 0) return b === 0;
    return Math.abs(b - a) / Math.abs(a) <= fact.tolerancePct / 100;
  }
  return String(a).toLowerCase() === String(b).toLowerCase();
}

function distinctValues(fact: CanonicalFact, values: Tier2eValue[]): Array<string | number> {
  const distinct: Array<string | number> = [];
  for (const { value } of values) {
    if (!distinct.some((d) => valuesEquivalent(fact, d, value))) distinct.push(value);
  }
  return distinct;
}

/**
 * S1 intent-less fallback — a set of distinct, finite, positive MW values can
 * be SORTED into a strictly non-decreasing sequence (e.g. 250 → 500 → 1000),
 * which is a legitimate CUMULATIVE phase ramp under the codebase's convention,
 * NOT a within-report conflict. Used ONLY for capacity and ONLY when no intent
 * SSOT is available to corroborate against. Any finite positive set qualifies
 * (a deduped numeric set is non-decreasing once sorted) — this is the
 * deliberately-permissive "ramp consistent" branch the plan calls for; a
 * genuine drift surfaces instead via the SSOT branch when intent IS present.
 */
function isMonotonicRamp(values: number[]): boolean {
  if (values.length < 2) return true;
  return values.every((v) => Number.isFinite(v) && v > 0);
}

function buildMentions(values: Tier2eValue[]): Tier2eMention[] {
  return values.map((v) => ({
    value: v.value,
    lineNumber: v.lineNumber,
    excerpt: v.line,
  }));
}

/**
 * Tier-2e — within-report numeric consistency.
 *
 * S1/S3 hard fix (non-weakening): the old check flattened every MW/acre/km line
 * into one bucket, deduped at a flat 1%, and hard-blocked on ≥2 survivors — with
 * ZERO phase-, unit-, zero- or field-awareness. Under the codebase's cumulative
 * phase convention a per-phase capacity (276 at Phase-1) and the ultimate
 * build-out (1000) legitimately co-exist, so this was a systematic false block.
 *
 * The fix is ADDITIVE and NON-WEAKENING:
 *   - single-valued facts (FEMA flood zone) keep the STRICT distinct-set block;
 *   - multi-valued facts (capacity, acreage, interconnect distance) drop
 *     ≤0 / non-finite mentions, route comparisons through `valuesWithinTolerance`
 *     (unit-aware), then CORROBORATE the surviving distinct set:
 *       • against the phase SSOT (ultimate + per-phase ramp) when present, OR
 *       • for capacity with no SSOT, a monotonic-ramp fallback (a non-decreasing
 *         MW sequence is consistent).
 *   - a value agreeing with NO SSOT anchor AND no co-existing ramp value STILL
 *     BLOCKS (or WARNs for warn-only facts) — a hallucinated drift is unchanged.
 */
function checkTier2eInternalNumbers(
  report: ReportDoc,
  findings: SiteReportFinding[],
  ctx?: ReportAuditContext,
): void {
  const text = reportFullText(report);
  const lines = text.split('\n');

  for (const fact of CANONICAL_FACTS) {
    const values: Tier2eValue[] = [];
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i]!;
      if (!fact.contextRe.test(line)) continue;
      for (const m of canonicalFactMatches(fact, line)) {
        // K/L — polysemy guard: the value's CLAUSE is about a different
        // quantity that shares this fact's unit (grid headroom MW,
        // fiber-lateral km). Window-scoped: an unrelated guard word elsewhere
        // on the line must not veto a legitimate nearby value.
        if (negativeContextNearMatch(fact.negativeContextRe, line, m.index, m[0]!.length)) continue;
        // K — a range endpoint ("200–500 MW") is a band, not a value claim.
        if (typeof m.index === 'number' && isRangeEndpoint(line, m.index)) continue;
        // Pronghorn 2026-08-17 — a comparison-BOUND prefix ("(<50 MW)",
        // "under 10 MW") states a bound, not a value claim. Syntax-level and
        // subject-agnostic (shared guard, capacityPolysemy).
        if (isComparisonBoundPrefix(line, m.index)) continue;
        let v: string | number = m[1]!;
        if (fact.toNumber) v = fact.toNumber(m[1]!);
        // S1 — zero/sentinel guard: a 0 / non-finite numeric magnitude is "unknown"
        // ("0 MW of spare headroom", "0 km", a placeholder), never a claim. Drop it
        // BEFORE bucketing so it can neither create nor satisfy a distinct value.
        if (typeof v === 'number' && (!Number.isFinite(v) || v <= 0)) continue;
        values.push({ value: v, line: canonicalFactExcerpt(fact, line, m), lineNumber: i + 1 });
      }
    }
    if (values.length < 2) continue;

    const distinct = distinctValues(fact, values);
    if (distinct.length < 2) continue;

    // Single-valued facts (flood zone): two distinct values is a genuine conflict.
    if (!fact.multiValue) {
      const reconciledSingle = fact.internalKey
        ? ctx?.reconcileOverrides?.get(fact.internalKey)
        : undefined;
      findings.push({
        tier: 'Tier 2e — Internal number consistency',
        severity: reconciledSingle !== undefined ? 'info' : 'block',
        message: `${fact.label} cited with ${distinct.length} distinct values in same report: ${distinct.map(String).join(', ')}${
          reconciledSingle !== undefined
            ? ` — reconciled to ${reconciledSingle}; applies on next regeneration.`
            : ''
        }`,
        context: values.map((v) => `  - ${v.value} → "${v.line}"`).join('\n'),
        canonicalFactKey: fact.internalKey,
        mentions: buildMentions(values),
      });
      continue;
    }

    // Multi-valued facts — corroborate the distinct numeric set against the SSOT.
    const numericDistinct = distinct.filter((d): d is number => typeof d === 'number');

    // S1 — capacity phase SSOT: every distinct value that agrees with the
    // ultimate total OR any per-phase cumulative ramp value is corroborated.
    // We ALSO corroborate the SUM of the cumulative ramp: under the cumulative
    // phase convention that sum (e.g. 250+500+1000 = 1750 against a true 1000
    // ultimate) is the DOCUMENTED Holly-Ridge / Redhead double-count artifact —
    // a known, SSOT-DERIVED echo, not an arbitrary number. Recognizing it stops
    // the false block WITHOUT hiding a genuine conflict: any value that is
    // neither an anchor, a ramp value, nor that exact derived sum (e.g. a
    // hallucinated 1500) is still uncorroborated and STILL surfaces.
    const ssot = fact.id === 'totalCapacityMW' ? ctx?.capacitySsot : undefined;
    const anchors: number[] = capacityAnchors(ssot);

    let uncorroborated = numericDistinct;
    // Adversarial review 2026-08-13: the old intent-less fallback silently
    // dropped EVERY multi-value capacity set (isMonotonicRamp was a tautology
    // — any positive set sorts non-decreasing), which meant a genuine
    // within-report hallucination (1000 vs 1200 MW) never surfaced on sites
    // without a configured phase plan. The permissive product decision
    // (don't hard-block phased-but-unconfigured sites) stands, but the set
    // now surfaces as INFO — visible, never silent, never blocking.
    const rampAssumed =
      anchors.length === 0 && fact.id === 'totalCapacityMW' && isMonotonicRamp(numericDistinct);
    if (anchors.length > 0) {
      uncorroborated = numericDistinct.filter(
        (d) => !anchors.some((a) => valuesEquivalent(fact, a, d)),
      );
    }

    // Non-numeric distinct values (e.g. an acreage parse that fell through to a
    // string) are never corroboratable by the numeric SSOT — keep them strict.
    const nonNumericDistinct = distinct.filter((d) => typeof d !== 'number');

    // NON-WEAKENING corroboration semantics (mirrors R-78 best-match):
    //  - when a phase SSOT (or the monotonic-ramp fallback) corroborates EVERY
    //    distinct value, there is no conflict → no finding;
    //  - when ANY distinct value agrees with NO anchor/ramp value (genuine drift
    //    or a hallucination, e.g. a stray sum-double-count), the uncorroborated
    //    value(s) STILL surface — a value agreeing with nothing always blocks.
    const surviving = [...uncorroborated, ...nonNumericDistinct];
    if (surviving.length === 0) continue;

    // A user-recorded canonical SETTLES the fact: the conflict is resolved by
    // decision and the rewrite lands at the next regeneration — demote to info
    // (visible, never silent) instead of blocking on a decision already made.
    const reconciled = fact.internalKey ? ctx?.reconcileOverrides?.get(fact.internalKey) : undefined;
    const severity: SiteReportFinding['severity'] =
      reconciled !== undefined || rampAssumed ? 'info' : fact.warnOnly ? 'warn' : 'block';
    const ssotNote = anchors.length > 0
      ? ` (none match the phase SSOT: ${[...new Set(anchors)].join(', ')})`
      : rampAssumed
        ? ' (read as a cumulative phase ramp — configure project phases to verify)'
        : '';
    const reconciledNote =
      reconciled !== undefined ? ` — reconciled to ${reconciled}; applies on next regeneration.` : '';
    findings.push({
      tier: 'Tier 2e — Internal number consistency', severity,
      message: `${fact.label} cited with ${surviving.length} unreconciled value(s) in same report: ${surviving.map(String).join(', ')}${ssotNote}${reconciledNote}`,
      context: values.map((v) => `  - ${v.value} → "${v.line}"`).join('\n'),
      canonicalFactKey: fact.internalKey,
      mentions: buildMentions(values),
    });
  }
}

function checkTier2fCitationIntegrity(report: ReportDoc, findings: SiteReportFinding[]): void {
  const text = reportFullText(report);

  // Placeholder citation patterns
  const placeholderPatterns = [
    /\[Ref\s+tariff\]/gi, /\[Ref\s+environmental\]/gi, /\[Ref\s+water\]/gi,
    /\[Ref\s+power\]/gi, /\[Ref\s+TBD\]/gi, /\[Ref\s+TODO\]/gi,
    /\[Ref\s+\?+\]/gi, /\[Ref\s+placeholder\]/gi,
    /\[citation needed\]/gi, /\{\{[^}]+\}\}/g,
  ];
  let placeholderCount = 0;
  for (const re of placeholderPatterns) {
    const matches = text.match(re);
    if (matches) {
      placeholderCount += matches.length;
      findings.push({
        tier: 'Tier 2f — Placeholder citation', severity: 'block',
        message: `${matches.length}× "${matches[0]}" placeholder detected. Body has literal template tokens.`,
      });
    }
  }

  // Appendix bijection — every [Ref N] in body has a matching appendix entry
  const appendixStart = text.search(/^#{1,4}\s+(References|Appendix|Sources|Bibliography)/im);
  if (appendixStart > 0) {
    const body = text.slice(0, appendixStart);
    const appendix = text.slice(appendixStart);
    const bodyRefs = new Set<number>();
    for (const m of body.matchAll(/\[Ref\s+(\d+(?:\s*[-–]\s*\d+)?(?:\s*,\s*\d+(?:\s*[-–]\s*\d+)?)*)\]/gi)) {
      for (const tok of m[1].split(',')) {
        const trimmed = tok.trim();
        if (/^\d+$/.test(trimmed)) bodyRefs.add(parseInt(trimmed, 10));
        else {
          const rng = trimmed.match(/^(\d+)\s*[-–]\s*(\d+)$/);
          if (rng) for (let n = parseInt(rng[1], 10); n <= parseInt(rng[2], 10); n++) bodyRefs.add(n);
        }
      }
    }
    const appendixRefs = new Map<number, string>();
    for (const m of appendix.matchAll(/^\s*\[Ref\s+(\d+)\]\s*[—\-:]?\s*(.*?)\s*$/gim)) {
      appendixRefs.set(parseInt(m[1], 10), (m[2] || '').trim());
    }
    for (const n of bodyRefs) {
      const entry = appendixRefs.get(n);
      if (entry === undefined) {
        findings.push({
          tier: 'Tier 2f — Broken ref (missing appendix)', severity: 'block',
          message: `Body cites [Ref ${n}] but no appendix entry exists.`,
        });
      } else if (entry.length === 0) {
        findings.push({
          tier: 'Tier 2f — Broken ref (empty appendix)', severity: 'block',
          message: `Body cites [Ref ${n}] but appendix entry is empty.`,
        });
      }
    }
    // Orphan appendix entries
    for (const [n] of appendixRefs) {
      if (!bodyRefs.has(n)) {
        findings.push({
          tier: 'Tier 2f — Orphan appendix entry', severity: 'warn',
          message: `[Ref ${n}] in appendix but never cited in body.`,
        });
      }
    }
  }
}

/**
 * ALIAS FAMILIES (2026-08-13, Redhead PSR RCA) — section ids that are
 * DESIGNED mirrors of one canonical narrative. The PSR's canonical field is
 * `executive`; siteRagService deliberately copies it into `summary` and
 * `executiveSummary` so list cards and cross-type renderers work
 * (geminiPrimaryNarrative + the summary/executiveSummary fills). Identical
 * prose WITHIN one family is construction, not a stub — the live finding
 * blocked an 884-char real analysis for being correctly mirrored. Kept in
 * lockstep with sectionDepthGate's twin detector.
 */
export const SECTION_ALIAS_FAMILY: Record<string, string> = {
  executiveSummary: 'primary-narrative',
  summary: 'primary-narrative',
  executive: 'primary-narrative',
  appendix: 'appendix',
  sentimentAppendix: 'appendix',
};

function checkTier2kStubTemplate(report: ReportDoc, findings: SiteReportFinding[]): void {
  const sections = extractSections(report);
  const normalized = sections.map((s) => ({
    id: s.id,
    norm: normalizeStubProse(s.content),
  })).filter((s) => s.norm.length >= 30);
  const counts = new Map<string, string[]>();
  for (const s of normalized) {
    const existing = counts.get(s.norm) ?? [];
    existing.push(s.id);
    counts.set(s.norm, existing);
  }
  for (const [norm, ids] of counts) {
    // Count distinct alias FAMILIES sharing the prose — designed mirrors of
    // one canonical narrative collapse to a single family; a real template
    // stub (the 8-section pattern) repeats across ≥3 DISTINCT families and
    // still blocks.
    const families = new Set(ids.map((id) => SECTION_ALIAS_FAMILY[id] ?? id));
    if (families.size >= 3) {
      findings.push({
        tier: 'Tier 2k — Stub-template repeat', severity: 'block',
        message: `${ids.length} sections share identical prose: ${ids.join(', ')}. Template stub detected (Public Sentiment 8-section pattern).`,
        stubProse: norm,
        stubSectionIds: [...ids],
      });
    }
  }
}

function checkTier2lSectionDepth(report: ReportDoc, findings: SiteReportFinding[]): void {
  const thresholds = SECTION_DEPTH[report.reportType] ?? {};
  const sections = extractSections(report);
  const sectionMap = new Map(sections.map((s) => [s.id, s]));
  // Explicitly-omitted sections (evidence domain provably empty — see
  // reportGeneration/sectionOmissions) demote to warn: regeneration cannot
  // fill them, so a block demanding it is unpassable.
  const omitted = new Set(
    ((report as { qaFindings?: { sectionOmissions?: Array<{ sectionId: string }> } }).qaFindings
      ?.sectionOmissions ?? []).map((o) => o.sectionId),
  );
  const omissionNote = ' Explicitly omitted: evidence sources returned no facts — add sources.';
  for (const [sectionId, minChars] of Object.entries(thresholds)) {
    // J — PSR stores its appendix under `sentimentAppendix` (see the alias
    // table in sectionDepthGate); honor the alias so the check is passable.
    const section = sectionMap.get(sectionId)
      ?? (sectionId === 'appendix' ? sectionMap.get('sentimentAppendix') : undefined);
    const isOmitted = omitted.has(sectionId);
    if (!section) {
      findings.push({
        tier: 'Tier 2l — Section depth', severity: isOmitted ? 'warn' : 'block',
        message: `Section "${sectionId}" missing from report.${isOmitted ? omissionNote : ''}`,
      });
      continue;
    }
    const len = section.content.trim().length;
    if (len < minChars) {
      findings.push({
        tier: 'Tier 2l — Section depth', severity: isOmitted ? 'warn' : 'block',
        message: `Section "${sectionId}" has ${len} chars; min ${minChars}.${isOmitted ? omissionNote : ''}`,
      });
    }
  }
}

function checkTier2mCitationTelemetry(report: ReportDoc, findings: SiteReportFinding[], stats: ReportAudit['stats']): void {
  const text = reportFullText(report);
  const refMatches = text.match(/\[Ref\s+\d+(?:\s*[-–]\s*\d+)?(?:\s*,\s*\d+(?:\s*[-–]\s*\d+)?)*\]/gi) ?? [];
  const perMatches = text.match(/\(per\s+"[^"]+"\)/gi) ?? [];
  stats.refNCount = refMatches.length;
  stats.perFilenameCount = perMatches.length;
  // Modern formats (Codex review on #135) — Tier-2b counts numeric [n]
  // markers and structured refIds now; without mirroring them here a
  // correctly cited modern report trades the density warn for a Tier-2m
  // "zero citations of any kind" warn. Same rulers, same formats.
  const numericMatches = text.match(/\[\d{1,3}(?:,\s*\d{1,3})*\]/g) ?? [];
  const structuredCount = countStructuredRefIds(
    Object.fromEntries(extractSections(report)
      .map((s) => [s.id, (report as unknown as Record<string, unknown>)[s.id]])),
    maxResolvableRefId(report),
  );

  if (stats.refNCount === 0 && stats.perFilenameCount === 0
    && numericMatches.length === 0 && structuredCount === 0
    && stats.bodyChars > 1000) {
    // M — one defect, one finding: with zero citations the Tier-2b density
    // check has already warned ("density 0.00"); a second row saying the
    // same thing in different words double-counts the defect. Only surface
    // the telemetry warn when 2b did NOT fire (e.g. a report type without a
    // density threshold).
    if (findings.some((f) => f.tier.startsWith('Tier 2b'))) return;
    findings.push({
      tier: 'Tier 2m — Citation telemetry', severity: 'warn',
      message: `Report has ${stats.bodyChars} chars but zero citations of any kind (no [Ref N], no (per "filename")).`,
    });
  }
}

function checkTier2nCaveatHonesty(report: ReportDoc, findings: SiteReportFinding[]): void {
  const text = reportFullText(report);
  const caveatHeading = /^#{2,4}\s+(What this report does not know|Caveats|Limitations|Known limitations)\s*$/im;
  const headingMatch = text.match(caveatHeading);
  if (!headingMatch) return;
  const start = (headingMatch.index ?? 0) + headingMatch[0].length;
  const nextHeading = text.slice(start).search(/^#{2,4}\s+/im);
  const caveatBody = (nextHeading > 0 ? text.slice(start, start + nextHeading) : text.slice(start)).trim();

  const falseHonestyPatterns = [
    /all (?:data )?sources (?:are )?reporting normally/i,
    /no (?:known )?(?:caveats?|limitations?)/i,
    /all (?:providers? )?(?:returned|reporting) normal/i,
    /no (?:data )?(?:issues?|gaps?|failures?) (?:detected|identified)/i,
  ];
  for (const re of falseHonestyPatterns) {
    if (re.test(caveatBody)) {
      findings.push({
        tier: 'Tier 2n — Caveat honesty', severity: 'warn',
        message: `Caveat section uses "${re.source}"-style language. Verify against providerResults — if any provider was partial/skipped/baseline, this is false honesty.`,
        context: caveatBody.slice(0, 200),
      });
    }
  }
}

function checkTier4CustomBriefCoverage(report: ReportDoc, findings: SiteReportFinding[]): void {
  if (report.reportType !== 'custom-brief') return;
  const answers = report.customBriefAnswers ?? [];
  const totalQs = report.customBriefQuestions?.length ?? answers.length;
  let populated = 0;
  let qNumDups = 0;
  const seenNumbers = new Set<number>();
  for (const a of answers) {
    if ((a.findings?.length ?? 0) > 80) populated += 1;
    if (typeof a.questionNumber === 'number') {
      if (seenNumbers.has(a.questionNumber)) qNumDups += 1;
      seenNumbers.add(a.questionNumber);
    }
  }
  const coverage = totalQs > 0 ? populated / totalQs : 0;
  if (coverage < 0.8) {
    findings.push({
      tier: 'Tier 4 — Custom Brief coverage', severity: 'block',
      message: `Only ${populated} of ${totalQs} user questions have substantive findings (${(coverage * 100).toFixed(0)}%). Demo gate: ≥ 80%.`,
    });
  }
  if (qNumDups > 0) {
    findings.push({
      tier: 'Tier 4 — Custom Brief Q-number dup', severity: 'block',
      message: `${qNumDups} duplicate questionNumber values across answers — would render "Q1./Q1./Q1." in TOC.`,
    });
  }
}

/* ──────────────────────────────────────────────────────────────────────
 * Tier 3 — Cross-report canonical-fact reconciliation
 * ────────────────────────────────────────────────────────────────────── */

/** D — a cross-report mention: value + the report and line it came from. */
interface CrossReportMention {
  reportType: string;
  value: string | number;
  line: string;
  lineNumber: number;
}

function extractCanonicalFactsFromReport(report: ReportDoc): Map<string, CrossReportMention[]> {
  const text = reportFullText(report);
  const out = new Map<string, CrossReportMention[]>();
  const lines = text.split('\n');
  for (const fact of CANONICAL_FACTS) {
    const values: CrossReportMention[] = [];
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i]!;
      if (!fact.contextRe.test(line)) continue;
      for (const m of canonicalFactMatches(fact, line)) {
        // K/L — polysemy guard, mirrored from Tier-2e; window-scoped so an
        // unrelated guard word elsewhere on the line can't veto a real value.
        if (negativeContextNearMatch(fact.negativeContextRe, line, m.index, m[0]!.length)) continue;
        // K — a range endpoint ("200–500 MW") is a band, not a value claim.
        if (typeof m.index === 'number' && isRangeEndpoint(line, m.index)) continue;
        // Pronghorn 2026-08-17 — comparison-BOUND prefix, mirrored from Tier-2e.
        if (isComparisonBoundPrefix(line, m.index)) continue;
        let v: string | number = m[1];
        if (fact.toNumber) v = fact.toNumber(m[1]);
        // S1 — zero/sentinel guard, mirrored from Tier-2e.
        if (typeof v === 'number' && (!Number.isFinite(v) || v <= 0)) continue;
        if (!values.some((existing) => existing.value === v)) {
          values.push({
            reportType: report.reportType,
            value: v,
            line: canonicalFactExcerpt(fact, line, m),
            lineNumber: i + 1,
          });
        }
      }
    }
    if (values.length > 0) out.set(fact.id, values);
  }
  return out;
}

function valueMatches(fact: CanonicalFact, a: string | number, b: string | number): boolean {
  if (typeof a === 'number' && typeof b === 'number') {
    if (a === 0) return b === 0;
    return Math.abs(b - a) / Math.abs(a) <= fact.tolerancePct / 100;
  }
  return String(a).toLowerCase() === String(b).toLowerCase();
}

/**
 * Tier-3 — cross-report canonical-fact reconciliation.
 *
 * A (2026-08-12, Redhead): this check ran phase-blind long after Tier-2e was
 * taught the phase SSOT, so per-phase / grid-quantity MW mentions that passed
 * within-report corroboration were re-flagged as cross-report "dissenters"
 * against the single ultimate value. It now corroborates against the SAME
 * `capacityAnchors` set as Tier-2e (non-weakening: a value matching no anchor
 * still blocks), respects a user-recorded reconcile override (settles to
 * info, mirroring Tier-2e/R-79), mirrors `warnOnly` severity for multi-value
 * facts, and carries structured mentions + picker candidates (D/E).
 */
export function reconcileAcrossReports(
  siteAudit: SiteReportAuditResult,
  reports: ReportDoc[],
  ctx?: ReportAuditContext,
): void {
  for (const fact of CANONICAL_FACTS) {
    const perReport = new Map<string, CrossReportMention[]>();
    for (const r of reports) {
      const facts = extractCanonicalFactsFromReport(r);
      const vals = facts.get(fact.id);
      if (vals && vals.length > 0) perReport.set(r.reportType, vals);
    }
    if (perReport.size < 2) continue;

    // Source of truth
    let truth: string | number | undefined;
    let truthSource = '';
    if (fact.id === 'totalCapacityMW') {
      // Phased sites: the ultimate (final-phase cumulative) capacity is the
      // truth — phased intents often leave `powerRequirementMW` null, so
      // prefer the phased total when populated.
      if (siteAudit.intent?.totalCapacityMW != null) {
        truth = siteAudit.intent.totalCapacityMW;
        truthSource = 'projectIntent.phases (ultimate cumulative)';
      } else if (siteAudit.intent?.powerRequirementMW != null) {
        truth = siteAudit.intent.powerRequirementMW;
        truthSource = 'projectIntent.powerRequirementMW';
      }
    }

    // Collect distinct values across reports
    const allValues: CrossReportMention[] = [];
    for (const vals of perReport.values()) allValues.push(...vals);
    const distinct: Array<string | number> = [];
    for (const { value } of allValues) {
      if (!distinct.some((d) => valueMatches(fact, d, value))) distinct.push(value);
    }
    if (distinct.length < 2) continue;

    // A — phase-SSOT corroboration for capacity: every distinct value that
    // agrees with ANY anchor (ultimate, per-phase cumulative, ramp-sum echo)
    // is consistent, not a dissent. Values matching no anchor still surface.
    const anchors = fact.id === 'totalCapacityMW' ? capacityAnchors(ctx?.capacitySsot) : [];
    const corroborated = (v: string | number): boolean =>
      (typeof v === 'number' && anchors.some((a) => valueMatches(fact, a, v)))
      || (truth !== undefined && valueMatches(fact, truth, v));

    const survivingDistinct = anchors.length > 0 || truth !== undefined
      ? distinct.filter((d) => !corroborated(d))
      : distinct;
    // Everything corroborated → phase-consistent, no conflict.
    if (survivingDistinct.length === 0) continue;

    const dissenters = allValues.filter((v) => !corroborated(v.value));
    const dissentingTypes = [...new Set(dissenters.map((d) => d.reportType))];

    // A/E — a user-recorded canonical settles the fact (mirrors Tier-2e/R-79):
    // demote to info, visible until the rewrite lands at next regeneration.
    const reconciled = fact.internalKey ? ctx?.reconcileOverrides?.get(fact.internalKey) : undefined;
    const severity: SiteReportFinding['severity'] =
      reconciled !== undefined ? 'info' : fact.warnOnly ? 'warn' : 'block';

    // E — picker candidates, truth first, then surviving values.
    const candidateValues = [
      ...(truth !== undefined ? [String(truth)] : []),
      ...survivingDistinct.map(String),
    ];
    const reconcileCandidates = candidateValues.filter((v, i) => v !== '' && candidateValues.indexOf(v) === i);

    siteAudit.crossReportFindings.push({
      tier: 'Tier 3 — Cross-report canonical fact',
      severity,
      message: `${fact.label} disagrees across reports. Unreconciled values: ${survivingDistinct.map(String).join(', ')}.${
        truth !== undefined ? ` Source of truth: ${truth} (${truthSource}).` : ''
      }${dissentingTypes.length > 0 ? ` Dissenters: ${dissentingTypes.join(', ')}.` : ''}${
        reconciled !== undefined ? ` — reconciled to ${reconciled}; applies on next regeneration.` : ''
      }`,
      // D — per-mention provenance so the reviewer can judge each citation.
      context: allValues.map((v) => `  - ${v.reportType}: ${v.value} → "${v.line}"`).join('\n'),
      canonicalFactKey: fact.internalKey,
      mentions: allValues.map((v) => ({ value: v.value, lineNumber: v.lineNumber, excerpt: v.line })),
      ...(reconcileCandidates.length >= 2 ? { reconcileCandidates } : {}),
    });
  }
}

/* ──────────────────────────────────────────────────────────────────────
 * Per-report audit
 * ────────────────────────────────────────────────────────────────────── */

/**
 * J — is this report a body-less shell? True when the loader delivered only
 * metadata (`_metadataOnly`, e.g. a dead GCS credential) or when every
 * section is empty and there is no markdown body. Auditing a shell produced
 * 16 per-section blocks for ONE underlying defect (Redhead PSR, 2026-08-11).
 */
export function isReportBodyEmpty(report: ReportDoc): boolean {
  if ((report as { _metadataOnly?: boolean })._metadataOnly === true) return true;
  const md = ((report as { markdown?: string }).markdown ?? '').trim();
  if (md.length > 0) return false;
  const sections = extractSections(report);
  return sections.every((s) => s.content.trim().length === 0);
}

/**
 * J — PSR degraded mode (D2/R-76): a sentiment report persisted with the
 * explicit unavailable sentinel is a KNOWN state, not 8 section defects.
 */
function isUnavailablePsr(report: ReportDoc): boolean {
  if (report.reportType !== 'public-sentiment') return false;
  const r = report as {
    publicSentimentStatus?: string;
    __status?: string;
    executive?: string;
  };
  if (r.publicSentimentStatus === 'unavailable' || r.__status === 'unavailable') return true;
  return (r.executive ?? '').trim() === 'INSUFFICIENT_DATA';
}

export function auditReport(report: ReportDoc, ctx?: ReportAuditContext): ReportAudit {
  const findings: SiteReportFinding[] = [];
  const text = reportFullText(report);
  const stats: ReportAudit['stats'] = {
    bodyChars: text.length,
    sections: extractSections(report).length,
    refNCount: 0,
    perFilenameCount: 0,
    placeholders: 0,
  };

  // J — one defect, one finding: a body-less report gets a single block and
  // no per-section audit (every downstream check would fire vacuously —
  // missing sections, 0 chars, zero citations, zero vocab hits — 17 findings
  // for one cause). Same for the PSR unavailable degraded mode (a warn: the
  // state is designed and honest, the fix is data, not regeneration).
  const metadataOnly = (report as { _metadataOnly?: boolean })._metadataOnly === true;
  if (isReportBodyEmpty(report)) {
    findings.push({
      tier: 'Tier 1 — Report body unavailable', severity: 'block',
      message: metadataOnly
        ? 'Report body could not be loaded (metadata-only) — the audit cannot see its content. Check storage credentials, then re-open or regenerate.'
        : 'Report body is empty — generation produced no content. Regenerate the report; if it stays empty, escalate as a platform bug.',
    });
    return { reportId: report.id, reportType: report.reportType, findings, stats };
  }
  if (isUnavailablePsr(report)) {
    const reason = (report as { publicSentimentUnavailableReason?: string; __reason?: string });
    findings.push({
      tier: 'Tier 0 — Sentiment data unavailable', severity: 'warn',
      message: `Public sentiment data unavailable for this site — ${
        reason.publicSentimentUnavailableReason ?? reason.__reason ?? 'provider data insufficient or narrative call failed'
      }. The report carries the explicit unavailable sentinel; section-depth checks do not apply.`,
    });
    return { reportId: report.id, reportType: report.reportType, findings, stats };
  }

  checkTier1HardFails(report, findings);
  checkTier2bCitationDensity(report, findings);
  checkTier2dTopicalDrift(report, findings);
  checkTier2eInternalNumbers(report, findings, ctx);
  checkTier2fCitationIntegrity(report, findings);
  checkTier2kStubTemplate(report, findings);
  checkTier2lSectionDepth(report, findings);
  checkTier2mCitationTelemetry(report, findings, stats);
  checkTier2nCaveatHonesty(report, findings);
  checkTier4CustomBriefCoverage(report, findings);

  const qa = (report as { qaFindings?: {
    providerPrecedenceViolations?: ReportAudit['precedenceViolations'];
    internalNumberFindings?: ReportAudit['internalNumberFindings'];
  } }).qaFindings;
  const precedenceViolations = qa?.providerPrecedenceViolations;
  const storedInternal = qa?.internalNumberFindings;
  const internalNumberFindings = storedInternal?.length ? storedInternal : undefined;

  return {
    reportId: report.id,
    reportType: report.reportType,
    findings,
    precedenceViolations,
    internalNumberFindings,
    stats,
  };
}
